import { ABI_VERSION, INPUT_LAYOUTS, INPUT_MAGIC, INSTRUCTION_FLAG_MASK, INSTRUCTION_FLAG_OPTIONAL, INSTRUCTION_HEADER_BYTES, INSTRUCTION_LENGTH_ESCAPE, KEYBOARD_CODES, KEYBOARD_KEY_NAMES, MINIMUM_READABLE_ABI_VERSION, InputOpcode, MAX_INPUT_BYTES, MAX_INPUT_INSTRUCTIONS, MAX_WORD_BOUNDARIES, MAX_RESOURCE_BYTES, PROTOCOL_ALIGNMENT, STREAM_HEADER_BYTES, } from "./generated";
const MAX_U64 = 0xffffffffffffffffn;
const MAX_SCROLL_DELTA = 1_000_000;
const MAX_SCROLL_DELTA_MICROS = 1_000_000;
/**
 * Marks a wheel sample as a high-precision delta such as a trackpad gesture.
 *
 * High-precision deltas are already smooth and already carry platform momentum,
 * so Core applies them one-to-one. Samples without this bit are discrete wheel
 * notches, which browsers animate rather than jump.
 */
export const EVENT_FLAG_PRECISE_WHEEL = 1;
/** Every event flag bit defined by this ABI version. */
export const EVENT_FLAG_MASK = EVENT_FLAG_PRECISE_WHEEL;
/** Key event flag: the press is an auto-repeat rather than a fresh one. */
export const KEY_FLAG_REPEAT = 1;
/** Every key flag bit this ABI version defines. */
export const KEY_FLAG_MASK = KEY_FLAG_REPEAT;
const utf8Encoder = new TextEncoder();
const utf8Decoder = new TextDecoder("utf-8", { fatal: true, ignoreBOM: true });
/** Visual edge preference at a browser-facing UTF-16 position. */
export var InputAffinity;
(function (InputAffinity) {
    InputAffinity[InputAffinity["Upstream"] = 0] = "Upstream";
    InputAffinity[InputAffinity["Downstream"] = 1] = "Downstream";
})(InputAffinity || (InputAffinity = {}));
const INPUT_AFFINITIES = new Set([InputAffinity.Upstream, InputAffinity.Downstream]);
/**
 * Every event kind Core reports, as values rather than only as a type.
 *
 * A second hand-written copy of this list is how `keydown` came to be rejected
 * by the Worker transport while the encoder happily produced it: pressing a key
 * on the canvas killed the Worker as a protocol violation.
 */
export const INPUT_EVENT_KINDS = [
    "blur",
    "click",
    "contextmenu",
    "focus",
    "focusin",
    "focusout",
    "gotpointercapture",
    "keydown",
    "keyup",
    "lostpointercapture",
    "pointercancel",
    "pointerdown",
    "pointerenter",
    "pointerleave",
    "pointermove",
    "pointerout",
    "pointerover",
    "pointerup",
    "wheel",
];
/** Whether `value` is an event kind Core can report. */
export function isInputEventKind(value) {
    return INPUT_EVENT_KINDS.includes(value);
}
/** Deterministic Input Stream contract violation. */
export class InputStreamError extends Error {
    constructor(message, options) {
        super(message, options);
        this.name = "InputStreamError";
    }
}
/** Encodes one canonical little-endian Input Stream transaction. */
export function encodeInputBatch(batch) {
    assertU32(batch.frameSeq, "frameSeq");
    if (batch.commands.length + 1 > MAX_INPUT_INSTRUCTIONS) {
        fail("input instruction count exceeds limit");
    }
    const writer = new ByteWriter();
    writer.u32(INPUT_MAGIC);
    writer.u16(ABI_VERSION);
    writer.u16(STREAM_HEADER_BYTES);
    writer.u32(0);
    writer.u32(0);
    for (const command of batch.commands)
        encodeCommand(writer, command);
    writer.instruction(InputOpcode.Commit);
    writer.u32(batch.frameSeq);
    const bytes = writer.finish();
    if (bytes.byteLength > MAX_INPUT_BYTES)
        fail("input stream exceeds maximum size");
    const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
    view.setUint32(8, bytes.byteLength, true);
    view.setUint32(12, batch.commands.length + 1, true);
    return bytes;
}
/** Decodes untrusted bytes for recording, replay, and diagnostics. */
export function decodeInputBatch(input) {
    if (input.byteLength > MAX_INPUT_BYTES)
        fail("input stream exceeds maximum size");
    if (input.byteLength % PROTOCOL_ALIGNMENT !== 0)
        fail("input stream is not aligned");
    const reader = new ByteReader(input);
    if (reader.u32() !== INPUT_MAGIC)
        fail("wrong input stream magic");
    // Newer producers stay readable through the self-describing instruction
    // framing; anything older than it cannot be stepped through safely.
    if (reader.u16() < MINIMUM_READABLE_ABI_VERSION)
        fail("unsupported input ABI version");
    if (reader.u16() !== STREAM_HEADER_BYTES)
        fail("invalid input header length");
    if (reader.u32() !== input.byteLength)
        fail("declared input length does not match input");
    const declaredCount = reader.u32();
    if (declaredCount > MAX_INPUT_INSTRUCTIONS)
        fail("input instruction count exceeds limit");
    if (declaredCount > Math.floor(reader.remaining / INSTRUCTION_HEADER_BYTES)) {
        fail("input instruction count cannot fit in remaining bytes");
    }
    const commands = [];
    let actualCount = 0;
    let frameSeq;
    while (reader.remaining > 0) {
        if (frameSeq !== undefined)
            fail("Commit must be the last input instruction");
        const offset = reader.offset;
        const header = reader.instruction();
        actualCount += 1;
        // Skipping is the producer's call: dropping an unmarked input command
        // could silently change what the user's gesture did.
        if (!isKnownOpcode(InputOpcode, header.opcode)) {
            if (!header.optional)
                fail(`unknown input opcode ${String(header.opcode)}`);
            reader.seekTo(header.end);
            continue;
        }
        const opcode = header.opcode;
        if (opcode === InputOpcode.Commit) {
            frameSeq = reader.u32();
            validateInstructionSize(opcode, offset, reader.offset);
        }
        else {
            commands.push(decodeCommand(reader, opcode));
            validateInstructionSize(opcode, offset, reader.offset);
        }
        if (reader.offset !== header.end)
            fail("instruction length does not match its payload");
    }
    if (actualCount !== declaredCount)
        fail("input instruction count does not match input");
    if (frameSeq === undefined)
        fail("input stream is missing Commit");
    return { frameSeq, commands };
}
function encodeCommand(writer, command) {
    const opcode = opcodeFor(command);
    writer.instruction(opcode);
    switch (command.type) {
        case "focusEditable":
        case "blurEditable":
        case "scrollBegin":
        case "scrollEnd":
        case "scrollCancel":
            assertU32(command.nodeId, "scroll nodeId");
            writer.u32(command.nodeId);
            return;
        case "scrollDelta":
            assertU32(command.nodeId, "scroll nodeId");
            assertScrollDelta(command.deltaX, "scroll deltaX");
            assertScrollDelta(command.deltaY, "scroll deltaY");
            if (!Number.isInteger(command.elapsedMicros) ||
                command.elapsedMicros < 1 ||
                command.elapsedMicros > MAX_SCROLL_DELTA_MICROS) {
                fail("scroll delta elapsed time is invalid");
            }
            writer.u32(command.nodeId);
            writer.f32(command.deltaX);
            writer.f32(command.deltaY);
            writer.u32(command.elapsedMicros);
            return;
        case "setScrollVelocity":
            assertU32(command.nodeId, "scroll nodeId");
            assertScrollDelta(command.velocityX, "scroll velocityX");
            assertScrollDelta(command.velocityY, "scroll velocityY");
            writer.u32(command.nodeId);
            writer.f32(command.velocityX);
            writer.f32(command.velocityY);
            return;
        case "scrollTo":
            assertU32(command.nodeId, "scroll nodeId");
            assertScrollDelta(command.x, "scroll x");
            assertScrollDelta(command.y, "scroll y");
            writer.u32(command.nodeId);
            writer.f32(command.x);
            writer.f32(command.y);
            return;
        case "scrollBy":
            assertU32(command.nodeId, "scroll nodeId");
            assertScrollDelta(command.deltaX, "scroll deltaX");
            assertScrollDelta(command.deltaY, "scroll deltaY");
            writer.u32(command.nodeId);
            writer.f32(command.deltaX);
            writer.f32(command.deltaY);
            return;
        case "requestCharacterBounds":
            assertU32(command.nodeId, "editable nodeId");
            assertU32(command.start, "character bounds start");
            assertU32(command.end, "character bounds end");
            if (command.start > command.end)
                fail("character bounds range is reversed");
            writer.u32(command.nodeId);
            writer.u32(command.start);
            writer.u32(command.end);
            return;
        case "placeCaret":
            assertU32(command.nodeId, "editable nodeId");
            if (!Number.isFinite(command.x) || !Number.isFinite(command.y)) {
                fail("caret placement coordinate is invalid");
            }
            writer.u32(command.nodeId);
            writer.f32(command.x);
            writer.f32(command.y);
            writer.u32((command.extend ? 1 : 0) | (command.word ? 2 : 0));
            return;
        case "setWordBoundaries": {
            assertU32(command.nodeId, "editable nodeId");
            if (command.boundaries.length > MAX_WORD_BOUNDARIES)
                fail("too many word boundaries");
            let previous = -1;
            for (const offset of command.boundaries) {
                assertU32(offset, "word boundary offset");
                // Ascending and unique keeps one segmentation one byte sequence.
                if (offset <= previous)
                    fail("word boundaries must ascend without duplicates");
                previous = offset;
            }
            writer.u32(command.nodeId);
            writer.u32(Number(command.baseRevision & 0xffffffffn));
            writer.u32(Number((command.baseRevision >> 32n) & 0xffffffffn));
            writer.u32(command.boundaries.length);
            for (const offset of command.boundaries)
                writer.u32(offset);
            return;
        }
        case "moveDocumentCaret":
            assertU32(command.nodeId, "document nodeId");
            writer.u32(command.nodeId);
            writer.u8(caretDirectionCode(command.direction));
            writer.u8(command.granularity === "word" ? 1 : 0);
            writer.u8(command.extend ? 1 : 0);
            writer.u8(0);
            return;
        case "moveCaret":
            assertU32(command.nodeId, "editable nodeId");
            writer.u32(command.nodeId);
            writer.u8(caretDirectionCode(command.direction));
            writer.u8(command.granularity === "word" ? 1 : 0);
            writer.u8(command.extend ? 1 : 0);
            writer.u8(0);
            return;
        case "dispatchEvent":
            assertU32(command.eventId, "eventId");
            validateEventFields(command);
            writer.u32(command.eventId);
            writer.u16(eventKindCode(command.kind));
            writer.u16(command.flags);
            writer.f32(command.x);
            writer.f32(command.y);
            writer.f32(command.deltaX);
            writer.f32(command.deltaY);
            writer.u32(command.buttons);
            writer.u32(command.modifiers);
            writer.u32(command.pointerId);
            writer.u32(command.elapsedMicros);
            writer.u8(pointerTypeCode(command.pointerType));
            writer.u8(command.isPrimary ? 1 : 0);
            writer.u16(0);
            writer.f32(command.pressure);
            writer.f32(command.tiltX);
            writer.f32(command.tiltY);
            writer.f32(command.width);
            writer.f32(command.height);
            return;
        case "dispatchKeyEvent":
            assertU32(command.eventId, "eventId");
            validateKeyFields(command);
            writer.u32(command.eventId);
            writer.u16(eventKindCode(command.kind));
            writer.u16(command.flags);
            writer.u16(command.keyCode);
            writer.u16(command.keyName);
            writer.u32(command.keyText);
            writer.u32(command.modifiers);
            writer.u32(command.elapsedMicros);
            return;
        case "setPointerCapture":
        case "releasePointerCapture":
            assertU32(command.eventId, "eventId");
            assertU32(command.pointerId, "pointerId");
            assertU32(command.nodeId, "capture nodeId");
            if (command.pointerId === 0)
                fail("pointer capture id must be non-zero");
            writer.u32(command.eventId);
            writer.u32(command.pointerId);
            writer.u32(command.nodeId);
            return;
        case "focusNode":
            assertU32(command.eventId, "eventId");
            assertU32(command.nodeId, "focus nodeId");
            writer.u32(command.eventId);
            writer.u32(command.nodeId);
            writer.u8(focusOriginCode(command.origin));
            writer.u8(0);
            writer.u16(0);
            return;
        case "blurNode":
            assertU32(command.eventId, "eventId");
            assertU32(command.nodeId, "focus nodeId");
            writer.u32(command.eventId);
            writer.u32(command.nodeId);
            return;
        case "resetInteraction":
            assertU32(command.eventId, "eventId");
            writer.u32(command.eventId);
            writer.u8(resetReasonCode(command.reason));
            writer.u8(0);
            writer.u16(0);
            return;
    }
    writer.target(command);
    switch (command.type) {
        case "replace":
            assertU32(command.start, "range start");
            assertU32(command.end, "range end");
            writer.u32(command.start);
            writer.u32(command.end);
            writer.text(command.text);
            return;
        case "insert":
        case "updateComposition":
            writer.text(command.text);
            return;
        case "setSelection":
            writer.position(command.selection.anchor);
            writer.position(command.selection.focus);
            writer.u8(command.selection.anchor.affinity);
            writer.u8(command.selection.focus.affinity);
            writer.u16(0);
            return;
        case "commitComposition":
            writer.u8(command.text === undefined ? 0 : 1);
            writer.u8(0);
            writer.u16(0);
            writer.text(command.text ?? "");
            return;
        case "setDocumentSelection": {
            const selection = command.selection;
            const kind = selection.kind === "text" ? 1 : selection.kind === "node" ? 2 : 3;
            const anchorKey = selection.kind === "text"
                ? selection.anchorKey
                : selection.kind === "node"
                    ? selection.key
                    : 0;
            const anchorOffset = selection.kind === "text" ? selection.anchorOffset : 0;
            const focusKey = selection.kind === "text" ? selection.focusKey : 0;
            const focusOffset = selection.kind === "text" ? selection.focusOffset : 0;
            const gapIndex = selection.kind === "gap" ? selection.index : 0;
            for (const [value, label] of [
                [anchorKey, "anchor key"],
                [anchorOffset, "anchor offset"],
                [focusKey, "focus key"],
                [focusOffset, "focus offset"],
                [gapIndex, "gap index"],
            ]) {
                assertU32(value, label);
            }
            writer.u8(kind);
            writer.u8(0);
            writer.u16(0);
            writer.u32(anchorKey);
            writer.u32(anchorOffset);
            writer.u32(focusKey);
            writer.u32(focusOffset);
            writer.u32(gapIndex);
            return;
        }
        case "editDocument":
            assertU32(command.style, "document mark style");
            assertU32(command.font, "document mark font");
            if (command.operation !== "insert" && command.text.length > 0) {
                fail("only an insertion carries document text");
            }
            writer.u8(documentOperationCode(command.operation));
            writer.u8(0);
            writer.u16(0);
            writer.u32(command.style);
            writer.u32(command.font);
            writer.text(command.text);
            return;
        case "setMarks":
            assertU32(command.start, "mark range start");
            assertU32(command.end, "mark range end");
            assertU32(command.style, "mark style");
            assertU32(command.font, "mark font");
            if (command.start > command.end)
                fail("mark range is reversed");
            writer.u32(command.start);
            writer.u32(command.end);
            writer.u32(command.style);
            writer.u32(command.font);
            return;
        case "setPendingMark":
            assertU32(command.mark?.style ?? 0, "pending mark style");
            assertU32(command.mark?.font ?? 0, "pending mark font");
            writer.u32(command.mark?.style ?? 0);
            writer.u32(command.mark?.font ?? 0);
            writer.u8(command.mark === undefined ? 0 : 1);
            writer.u8(0);
            writer.u16(0);
            return;
        default:
            return;
    }
}
function decodeCommand(reader, opcode) {
    switch (opcode) {
        case InputOpcode.Replace:
            return {
                ...reader.target(),
                type: "replace",
                start: reader.u32(),
                end: reader.u32(),
                text: reader.text(),
            };
        case InputOpcode.Insert:
            return { ...reader.target(), type: "insert", text: reader.text() };
        case InputOpcode.DeleteBackward:
            return { ...reader.target(), type: "deleteBackward" };
        case InputOpcode.DeleteForward:
            return { ...reader.target(), type: "deleteForward" };
        case InputOpcode.SetSelection: {
            const target = reader.target();
            const anchorOffset = reader.u32();
            const focusOffset = reader.u32();
            const anchorAffinity = reader.affinity();
            const focusAffinity = reader.affinity();
            reader.zeroes(2);
            return {
                ...target,
                type: "setSelection",
                selection: {
                    anchor: { offset: anchorOffset, affinity: anchorAffinity },
                    focus: { offset: focusOffset, affinity: focusAffinity },
                },
            };
        }
        case InputOpcode.BeginComposition:
            return { ...reader.target(), type: "beginComposition" };
        case InputOpcode.UpdateComposition:
            return { ...reader.target(), type: "updateComposition", text: reader.text() };
        case InputOpcode.CommitComposition: {
            const target = reader.target();
            const hasText = reader.u8();
            reader.zeroes(3);
            const text = reader.text();
            if (hasText === 0 && text.length === 0)
                return { ...target, type: "commitComposition" };
            if (hasText === 1)
                return { ...target, type: "commitComposition", text };
            if (hasText === 0)
                fail("absent composition text is non-empty");
            return fail("invalid composition text presence flag");
        }
        case InputOpcode.CancelComposition:
            return { ...reader.target(), type: "cancelComposition" };
        case InputOpcode.SetDocumentSelection: {
            const target = reader.target();
            const kind = reader.u8();
            reader.zeroes(3);
            const anchorKey = reader.u32();
            const anchorOffset = reader.u32();
            const focusKey = reader.u32();
            const focusOffset = reader.u32();
            const gapIndex = reader.u32();
            if (kind === 1) {
                if (gapIndex !== 0)
                    fail("text document selection has a gap index");
                return {
                    ...target,
                    type: "setDocumentSelection",
                    selection: { kind: "text", anchorKey, anchorOffset, focusKey, focusOffset },
                };
            }
            if (kind === 2) {
                if (anchorOffset !== 0 || focusKey !== 0 || focusOffset !== 0 || gapIndex !== 0) {
                    fail("node document selection has a payload");
                }
                return {
                    ...target,
                    type: "setDocumentSelection",
                    selection: { kind: "node", key: anchorKey },
                };
            }
            if (kind === 3) {
                if (anchorKey !== 0 || anchorOffset !== 0 || focusKey !== 0 || focusOffset !== 0) {
                    fail("gap document selection has a payload");
                }
                return {
                    ...target,
                    type: "setDocumentSelection",
                    selection: { kind: "gap", index: gapIndex },
                };
            }
            return fail("unknown document selection kind");
        }
        case InputOpcode.MoveDocumentCaret: {
            const nodeId = reader.u32();
            const direction = caretDirectionName(reader.u8());
            const granularityCode = reader.u8();
            if (granularityCode > 1)
                fail("document caret granularity is unknown");
            const extendCode = reader.u8();
            if (extendCode > 1)
                fail("document caret extend flag is unknown");
            if (reader.u8() !== 0)
                fail("document caret padding must be zero");
            return {
                type: "moveDocumentCaret",
                nodeId,
                direction,
                granularity: granularityCode === 1 ? "word" : "grapheme",
                extend: extendCode === 1,
            };
        }
        case InputOpcode.EditDocument: {
            const target = reader.target();
            const operation = documentOperationName(reader.u8());
            reader.zeroes(3);
            const style = reader.u32();
            const font = reader.u32();
            const text = reader.text();
            if (operation !== "insert" && text.length > 0) {
                fail("only an insertion carries document text");
            }
            return { ...target, type: "editDocument", operation, style, font, text };
        }
        case InputOpcode.SetMarks: {
            const target = reader.target();
            const start = reader.u32();
            const end = reader.u32();
            if (start > end)
                fail("mark range is reversed");
            return { ...target, type: "setMarks", start, end, style: reader.u32(), font: reader.u32() };
        }
        case InputOpcode.SetPendingMark: {
            const target = reader.target();
            const style = reader.u32();
            const font = reader.u32();
            const present = reader.u8();
            reader.zeroes(3);
            if (present === 0) {
                if (style !== 0 || font !== 0)
                    fail("absent pending mark has a payload");
                return { ...target, type: "setPendingMark" };
            }
            if (present !== 1)
                fail("invalid pending mark presence flag");
            return { ...target, type: "setPendingMark", mark: { style, font } };
        }
        case InputOpcode.BreakUndoGroup:
            return { ...reader.target(), type: "breakUndoGroup" };
        case InputOpcode.Undo:
            return { ...reader.target(), type: "undo" };
        case InputOpcode.Redo:
            return { ...reader.target(), type: "redo" };
        case InputOpcode.FocusEditable:
            return { type: "focusEditable", nodeId: reader.u32() };
        case InputOpcode.BlurEditable:
            return { type: "blurEditable", nodeId: reader.u32() };
        case InputOpcode.RequestCharacterBounds: {
            const nodeId = reader.u32();
            const start = reader.u32();
            const end = reader.u32();
            if (start > end)
                fail("character bounds range is reversed");
            return { type: "requestCharacterBounds", nodeId, start, end };
        }
        case InputOpcode.MoveCaret: {
            const nodeId = reader.u32();
            const direction = caretDirectionName(reader.u8());
            const granularityCode = reader.u8();
            if (granularityCode > 1)
                fail("caret movement granularity is unknown");
            const extendCode = reader.u8();
            if (extendCode > 1)
                fail("caret extend flag is unknown");
            if (reader.u8() !== 0)
                fail("caret movement padding must be zero");
            return {
                type: "moveCaret",
                nodeId,
                direction,
                granularity: granularityCode === 1 ? "word" : "grapheme",
                extend: extendCode === 1,
            };
        }
        case InputOpcode.PlaceCaret: {
            const nodeId = reader.u32();
            const x = reader.f32();
            const y = reader.f32();
            const flags = reader.u32();
            if (!Number.isFinite(x) || !Number.isFinite(y))
                fail("caret placement coordinate is invalid");
            if ((flags & ~0x03) !== 0)
                fail("caret placement flags are reserved");
            return {
                type: "placeCaret",
                nodeId,
                x,
                y,
                extend: (flags & 1) !== 0,
                word: (flags & 2) !== 0,
            };
        }
        case InputOpcode.SetWordBoundaries: {
            const nodeId = reader.u32();
            const low = BigInt(reader.u32());
            const high = BigInt(reader.u32());
            const declared = reader.u32();
            if (declared > MAX_WORD_BOUNDARIES)
                fail("too many word boundaries");
            // Bound against the bytes that remain before allocating.
            if (declared > Math.floor(reader.remaining / 4))
                fail("truncated input batch");
            const boundaries = [];
            let previous = -1;
            for (let index = 0; index < declared; index += 1) {
                const offset = reader.u32();
                if (offset <= previous)
                    fail("word boundaries must ascend without duplicates");
                previous = offset;
                boundaries.push(offset);
            }
            return {
                type: "setWordBoundaries",
                nodeId,
                baseRevision: low | (high << 32n),
                boundaries: Object.freeze(boundaries),
            };
        }
        case InputOpcode.ScrollBegin:
            return { type: "scrollBegin", nodeId: reader.u32() };
        case InputOpcode.ScrollDelta: {
            const nodeId = reader.u32();
            const deltaX = reader.f32();
            const deltaY = reader.f32();
            assertScrollDelta(deltaX, "scroll deltaX");
            assertScrollDelta(deltaY, "scroll deltaY");
            const elapsedMicros = reader.u32();
            if (elapsedMicros < 1 || elapsedMicros > MAX_SCROLL_DELTA_MICROS) {
                return fail("scroll delta elapsed time is invalid");
            }
            return { type: "scrollDelta", nodeId, deltaX, deltaY, elapsedMicros };
        }
        case InputOpcode.ScrollEnd:
            return { type: "scrollEnd", nodeId: reader.u32() };
        case InputOpcode.ScrollCancel:
            return { type: "scrollCancel", nodeId: reader.u32() };
        case InputOpcode.SetScrollVelocity: {
            const nodeId = reader.u32();
            const velocityX = reader.f32();
            const velocityY = reader.f32();
            assertScrollDelta(velocityX, "scroll velocityX");
            assertScrollDelta(velocityY, "scroll velocityY");
            return { type: "setScrollVelocity", nodeId, velocityX, velocityY };
        }
        case InputOpcode.ScrollTo: {
            const nodeId = reader.u32();
            const x = reader.f32();
            const y = reader.f32();
            assertScrollDelta(x, "scroll x");
            assertScrollDelta(y, "scroll y");
            return { type: "scrollTo", nodeId, x, y };
        }
        case InputOpcode.ScrollBy: {
            const nodeId = reader.u32();
            const deltaX = reader.f32();
            const deltaY = reader.f32();
            assertScrollDelta(deltaX, "scroll deltaX");
            assertScrollDelta(deltaY, "scroll deltaY");
            return { type: "scrollBy", nodeId, deltaX, deltaY };
        }
        case InputOpcode.DispatchEvent: {
            const eventId = reader.u32();
            const kind = eventKind(reader.u16());
            const flags = reader.u16();
            const command = {
                type: "dispatchEvent",
                eventId,
                kind,
                flags,
                x: reader.f32(),
                y: reader.f32(),
                deltaX: reader.f32(),
                deltaY: reader.f32(),
                buttons: reader.u32(),
                modifiers: reader.u32(),
                pointerId: reader.u32(),
                elapsedMicros: reader.u32(),
                pointerType: pointerType(reader.u8()),
                isPrimary: booleanByte(reader.u8(), "primary pointer flag"),
                ...readPointerGeometry(reader),
            };
            validateEventFields(command);
            return command;
        }
        case InputOpcode.DispatchKeyEvent: {
            const command = {
                type: "dispatchKeyEvent",
                eventId: reader.u32(),
                kind: eventKind(reader.u16()),
                flags: reader.u16(),
                keyCode: reader.u16(),
                keyName: reader.u16(),
                keyText: reader.u32(),
                modifiers: reader.u32(),
                elapsedMicros: reader.u32(),
            };
            validateKeyFields(command);
            return command;
        }
        case InputOpcode.SetPointerCapture:
        case InputOpcode.ReleasePointerCapture: {
            const eventId = reader.u32();
            const pointerId = reader.u32();
            const nodeId = reader.u32();
            if (pointerId === 0)
                return fail("pointer capture id must be non-zero");
            return {
                type: opcode === InputOpcode.SetPointerCapture ? "setPointerCapture" : "releasePointerCapture",
                eventId,
                pointerId,
                nodeId,
            };
        }
        case InputOpcode.FocusNode: {
            const eventId = reader.u32();
            const nodeId = reader.u32();
            const origin = focusOrigin(reader.u8());
            reader.zeroes(3);
            return { type: "focusNode", eventId, nodeId, origin };
        }
        case InputOpcode.BlurNode:
            return { type: "blurNode", eventId: reader.u32(), nodeId: reader.u32() };
        case InputOpcode.ResetInteraction: {
            const eventId = reader.u32();
            const reason = resetReason(reader.u8());
            reader.zeroes(3);
            return { type: "resetInteraction", eventId, reason };
        }
        default:
            return fail(`unexpected input opcode ${String(opcode)}`);
    }
}
function opcodeFor(command) {
    switch (command.type) {
        case "replace":
            return InputOpcode.Replace;
        case "insert":
            return InputOpcode.Insert;
        case "deleteBackward":
            return InputOpcode.DeleteBackward;
        case "deleteForward":
            return InputOpcode.DeleteForward;
        case "setSelection":
            return InputOpcode.SetSelection;
        case "beginComposition":
            return InputOpcode.BeginComposition;
        case "updateComposition":
            return InputOpcode.UpdateComposition;
        case "commitComposition":
            return InputOpcode.CommitComposition;
        case "cancelComposition":
            return InputOpcode.CancelComposition;
        case "setDocumentSelection":
            return InputOpcode.SetDocumentSelection;
        case "moveDocumentCaret":
            return InputOpcode.MoveDocumentCaret;
        case "editDocument":
            return InputOpcode.EditDocument;
        case "setMarks":
            return InputOpcode.SetMarks;
        case "setPendingMark":
            return InputOpcode.SetPendingMark;
        case "breakUndoGroup":
            return InputOpcode.BreakUndoGroup;
        case "undo":
            return InputOpcode.Undo;
        case "redo":
            return InputOpcode.Redo;
        case "focusEditable":
            return InputOpcode.FocusEditable;
        case "blurEditable":
            return InputOpcode.BlurEditable;
        case "requestCharacterBounds":
            return InputOpcode.RequestCharacterBounds;
        case "placeCaret":
            return InputOpcode.PlaceCaret;
        case "moveCaret":
            return InputOpcode.MoveCaret;
        case "setWordBoundaries":
            return InputOpcode.SetWordBoundaries;
        case "scrollBegin":
            return InputOpcode.ScrollBegin;
        case "scrollDelta":
            return InputOpcode.ScrollDelta;
        case "scrollEnd":
            return InputOpcode.ScrollEnd;
        case "scrollCancel":
            return InputOpcode.ScrollCancel;
        case "setScrollVelocity":
            return InputOpcode.SetScrollVelocity;
        case "scrollTo":
            return InputOpcode.ScrollTo;
        case "scrollBy":
            return InputOpcode.ScrollBy;
        case "dispatchEvent":
            return InputOpcode.DispatchEvent;
        case "dispatchKeyEvent":
            return InputOpcode.DispatchKeyEvent;
        case "setPointerCapture":
            return InputOpcode.SetPointerCapture;
        case "releasePointerCapture":
            return InputOpcode.ReleasePointerCapture;
        case "focusNode":
            return InputOpcode.FocusNode;
        case "blurNode":
            return InputOpcode.BlurNode;
        case "resetInteraction":
            return InputOpcode.ResetInteraction;
    }
}
function documentOperationCode(operation) {
    switch (operation) {
        case "deleteBackward":
            return 1;
        case "deleteForward":
            return 2;
        case "insert":
            return 3;
        case "split":
            return 4;
    }
}
function documentOperationName(code) {
    switch (code) {
        case 1:
            return "deleteBackward";
        case 2:
            return "deleteForward";
        case 3:
            return "insert";
        case 4:
            return "split";
        default:
            return fail("unknown document operation");
    }
}
function caretDirectionCode(direction) {
    switch (direction) {
        case "backward":
            return 1;
        case "forward":
            return 2;
        case "up":
            return 3;
        case "down":
            return 4;
        case "lineStart":
            return 5;
        case "lineEnd":
            return 6;
    }
}
function caretDirectionName(code) {
    switch (code) {
        case 1:
            return "backward";
        case 2:
            return "forward";
        case 3:
            return "up";
        case 4:
            return "down";
        case 5:
            return "lineStart";
        case 6:
            return "lineEnd";
        default:
            return fail("caret movement direction is unknown");
    }
}
function eventKindCode(kind) {
    switch (kind) {
        case "pointerdown":
            return 1;
        case "pointerup":
            return 2;
        case "pointermove":
            return 3;
        case "pointercancel":
            return 4;
        case "click":
            return 5;
        case "wheel":
            return 6;
        case "pointerover":
            return 7;
        case "pointerout":
            return 8;
        case "pointerenter":
            return 9;
        case "pointerleave":
            return 10;
        case "gotpointercapture":
            return 11;
        case "lostpointercapture":
            return 12;
        case "focus":
            return 13;
        case "blur":
            return 14;
        case "focusin":
            return 15;
        case "focusout":
            return 16;
        case "keydown":
            return 17;
        case "keyup":
            return 18;
        case "contextmenu":
            return 19;
    }
}
function eventKind(value) {
    switch (value) {
        case 1:
            return "pointerdown";
        case 2:
            return "pointerup";
        case 3:
            return "pointermove";
        case 4:
            return "pointercancel";
        case 5:
            return "click";
        case 6:
            return "wheel";
        case 7:
            return "pointerover";
        case 8:
            return "pointerout";
        case 9:
            return "pointerenter";
        case 10:
            return "pointerleave";
        case 11:
            return "gotpointercapture";
        case 12:
            return "lostpointercapture";
        case 13:
            return "focus";
        case 14:
            return "blur";
        case 15:
            return "focusin";
        case 16:
            return "focusout";
        case 17:
            return "keydown";
        case 18:
            return "keyup";
        case 19:
            return "contextmenu";
        default:
            return fail("unknown input event kind");
    }
}
/**
 * Rejects key samples that cannot describe a real `KeyboardEvent`.
 *
 * Core never interprets a key, so the identifiers only have to be inside the
 * table bounds this ABI version declares; anything above them came from a newer
 * producer or a corrupt stream.
 */
function validateKeyFields(command) {
    if (command.kind !== "keydown" && command.kind !== "keyup") {
        fail("key dispatch requires a key event kind");
    }
    if (!Number.isInteger(command.flags) || command.flags < 0 || command.flags > KEY_FLAG_MASK) {
        fail("key flag bits are reserved");
    }
    if (!Number.isInteger(command.keyCode) ||
        command.keyCode < 0 ||
        command.keyCode > KEYBOARD_CODES.length ||
        !Number.isInteger(command.keyName) ||
        command.keyName < 0 ||
        command.keyName > KEYBOARD_KEY_NAMES.length) {
        fail("key identifier is out of range");
    }
    // Unicode scalar values exclude the surrogate range; `key` is never a lone
    // surrogate, so a stream carrying one is malformed.
    if (!Number.isInteger(command.keyText) ||
        command.keyText < 0 ||
        command.keyText > 0x10ffff ||
        (command.keyText >= 0xd800 && command.keyText <= 0xdfff)) {
        fail("key text is not a Unicode scalar");
    }
    if (command.keyName !== 0 && command.keyText !== 0) {
        fail("key cannot be both named and printable");
    }
    if (!Number.isInteger(command.modifiers) || command.modifiers < 0 || command.modifiers > 0x0f) {
        fail("key modifier bits are reserved");
    }
    if (!Number.isInteger(command.elapsedMicros) ||
        command.elapsedMicros <= 0 ||
        command.elapsedMicros > MAX_SCROLL_DELTA_MICROS) {
        fail("key elapsed time is invalid");
    }
}
function validateEventFields(command) {
    if (command.kind === "pointerover" ||
        command.kind === "pointerout" ||
        command.kind === "pointerenter" ||
        command.kind === "gotpointercapture" ||
        command.kind === "lostpointercapture" ||
        command.kind === "focus" ||
        command.kind === "blur" ||
        command.kind === "focusin" ||
        command.kind === "focusout") {
        fail("synthetic event kind cannot be dispatched by the host");
    }
    if (command.kind === "keydown" || command.kind === "keyup") {
        fail("key events use dispatchKeyEvent");
    }
    if (!Number.isInteger(command.flags) || command.flags < 0 || command.flags > EVENT_FLAG_MASK) {
        fail("event flags are invalid");
    }
    for (const [value, label, maximum] of [
        [command.x, "event x", 1_000_000_000],
        [command.y, "event y", 1_000_000_000],
        [command.deltaX, "event deltaX", MAX_SCROLL_DELTA],
        [command.deltaY, "event deltaY", MAX_SCROLL_DELTA],
    ]) {
        if (!Number.isFinite(value) || Math.abs(value) > maximum)
            fail(`${label} is invalid`);
    }
    if (!Number.isInteger(command.buttons) || command.buttons < 0 || command.buttons > 0xffff) {
        fail("event buttons are invalid");
    }
    if (!Number.isInteger(command.modifiers) || command.modifiers < 0 || command.modifiers > 0x0f) {
        fail("event modifiers are invalid");
    }
    assertU32(command.pointerId, "event pointerId");
    const pointerEvent = command.kind === "pointerdown" ||
        command.kind === "pointerup" ||
        command.kind === "pointermove" ||
        command.kind === "pointercancel" ||
        command.kind === "pointerleave";
    if (pointerEvent !== (command.pointerId !== 0 && command.pointerType !== "none")) {
        fail("pointer event identity and type are inconsistent");
    }
    if (typeof command.isPrimary !== "boolean")
        fail("event isPrimary must be boolean");
    if (!Number.isFinite(command.pressure) || command.pressure < 0 || command.pressure > 1) {
        fail("event pressure is outside 0..=1");
    }
    for (const tilt of [command.tiltX, command.tiltY]) {
        if (!Number.isFinite(tilt) || tilt < -90 || tilt > 90)
            fail("event tilt is outside -90..=90");
    }
    for (const size of [command.width, command.height]) {
        if (!Number.isFinite(size) || size < 0 || size > 1_000_000) {
            fail("event contact size is invalid");
        }
    }
    if (!Number.isInteger(command.elapsedMicros) ||
        command.elapsedMicros < 1 ||
        command.elapsedMicros > MAX_SCROLL_DELTA_MICROS) {
        fail("event elapsed time is invalid");
    }
}
function pointerTypeCode(value) {
    switch (value) {
        case "none":
            return 0;
        case "mouse":
            return 1;
        case "pen":
            return 2;
        case "touch":
            return 3;
    }
}
function pointerType(value) {
    switch (value) {
        case 0:
            return "none";
        case 1:
            return "mouse";
        case 2:
            return "pen";
        case 3:
            return "touch";
        default:
            return fail("unknown input pointer type");
    }
}
function focusOriginCode(value) {
    return { pointer: 1, keyboard: 2, programmatic: 3, accessibility: 4 }[value];
}
function focusOrigin(value) {
    switch (value) {
        case 1:
            return "pointer";
        case 2:
            return "keyboard";
        case 3:
            return "programmatic";
        case 4:
            return "accessibility";
        default:
            return fail("unknown input focus origin");
    }
}
function resetReasonCode(value) {
    return { windowBlur: 1, documentHidden: 2, transportRecovery: 3, hostUnmount: 4 }[value];
}
function resetReason(value) {
    switch (value) {
        case 1:
            return "windowBlur";
        case 2:
            return "documentHidden";
        case 3:
            return "transportRecovery";
        case 4:
            return "hostUnmount";
        default:
            return fail("unknown interaction reset reason");
    }
}
function booleanByte(value, label) {
    if (value === 0)
        return false;
    if (value === 1)
        return true;
    return fail(`${label} is invalid`);
}
function readPointerGeometry(reader) {
    reader.zeroes(2);
    return {
        pressure: reader.f32(),
        tiltX: reader.f32(),
        tiltY: reader.f32(),
        width: reader.f32(),
        height: reader.f32(),
    };
}
class ByteWriter {
    #bytes = [];
    #instructionOpcode;
    #instructionStart = 0;
    instruction(opcode) {
        this.validateInstruction();
        this.#instructionOpcode = opcode;
        this.#instructionStart = this.#bytes.length;
        this.u8(opcode);
        this.u8(0);
        this.u16(0);
    }
    target(target) {
        assertU32(target.nodeId, "nodeId");
        assertU64(target.baseRevision, "baseRevision");
        this.u32(target.nodeId);
        this.u32(Number(target.baseRevision & 0xffffffffn));
        this.u32(Number(target.baseRevision >> 32n));
    }
    position(position) {
        assertU32(position.offset, "selection offset");
        assertAffinity(position.affinity);
        this.u32(position.offset);
    }
    text(value) {
        const bytes = utf8Encoder.encode(value);
        if (bytes.byteLength > MAX_RESOURCE_BYTES)
            fail("input text exceeds maximum size");
        this.u32(bytes.byteLength);
        this.bytes(bytes);
        this.pad();
    }
    u8(value) {
        if (!Number.isInteger(value) || value < 0 || value > 0xff)
            fail("value must be a u8");
        this.#bytes.push(value);
    }
    u16(value) {
        if (!Number.isInteger(value) || value < 0 || value > 0xffff)
            fail("value must be a u16");
        this.#bytes.push(value & 0xff, (value >>> 8) & 0xff);
    }
    u32(value) {
        assertU32(value, "value");
        this.#bytes.push(value & 0xff, (value >>> 8) & 0xff, (value >>> 16) & 0xff, (value >>> 24) & 0xff);
    }
    f32(value) {
        if (!Number.isFinite(value))
            fail("value must be a finite f32");
        const bytes = new Uint8Array(4);
        new DataView(bytes.buffer).setFloat32(0, value, true);
        this.bytes(bytes);
    }
    bytes(value) {
        for (const byte of value)
            this.#bytes.push(byte);
    }
    pad() {
        while (this.#bytes.length % PROTOCOL_ALIGNMENT !== 0)
            this.#bytes.push(0);
    }
    finish() {
        this.validateInstruction();
        if (this.#bytes.length % PROTOCOL_ALIGNMENT !== 0)
            fail("encoder produced misaligned input");
        return Uint8Array.from(this.#bytes);
    }
    /**
     * Closes the instruction that just ended, writing its length into the header.
     *
     * The length is unknown when the header goes out, so it is patched here and
     * no call site has to know it exists.
     */
    validateInstruction() {
        if (this.#instructionOpcode === undefined)
            return;
        validateInstructionSize(this.#instructionOpcode, this.#instructionStart, this.#bytes.length);
        this.#instructionOpcode = undefined;
        const start = this.#instructionStart;
        const length = this.#bytes.length - start;
        const words = length / PROTOCOL_ALIGNMENT;
        if (words < INSTRUCTION_LENGTH_ESCAPE) {
            this.#bytes[start + 2] = words & 0xff;
            this.#bytes[start + 3] = (words >>> 8) & 0xff;
            return;
        }
        this.#bytes[start + 2] = INSTRUCTION_LENGTH_ESCAPE & 0xff;
        this.#bytes[start + 3] = (INSTRUCTION_LENGTH_ESCAPE >>> 8) & 0xff;
        const total = length + PROTOCOL_ALIGNMENT;
        this.#bytes.splice(start + INSTRUCTION_HEADER_BYTES, 0, total & 0xff, (total >>> 8) & 0xff, (total >>> 16) & 0xff, (total >>> 24) & 0xff);
    }
}
class ByteReader {
    #input;
    #view;
    #offset = 0;
    constructor(input) {
        this.#input = input;
        this.#view = new DataView(input.buffer, input.byteOffset, input.byteLength);
    }
    get offset() {
        return this.#offset;
    }
    get remaining() {
        return this.#input.byteLength - this.#offset;
    }
    /** Reads one instruction header, including where the instruction ends. */
    instruction() {
        const offset = this.#offset;
        if (offset % PROTOCOL_ALIGNMENT !== 0)
            fail("instruction is not aligned");
        this.require(INSTRUCTION_HEADER_BYTES);
        const opcode = this.u8();
        const flags = this.u8();
        if ((flags & ~INSTRUCTION_FLAG_MASK) !== 0)
            fail("unsupported instruction flags");
        const words = this.u16();
        const length = words === INSTRUCTION_LENGTH_ESCAPE ? this.u32() : words * PROTOCOL_ALIGNMENT;
        const end = offset + length;
        if (length < INSTRUCTION_HEADER_BYTES || length % PROTOCOL_ALIGNMENT !== 0) {
            fail("instruction length is invalid");
        }
        if (end > this.#input.byteLength)
            fail("instruction length runs past the stream");
        return { opcode, optional: (flags & INSTRUCTION_FLAG_OPTIONAL) !== 0, end };
    }
    /** Moves the cursor forward to an instruction boundary. */
    seekTo(offset) {
        if (offset < this.#offset || offset > this.#input.byteLength)
            fail("invalid instruction skip");
        this.#offset = offset;
    }
    target() {
        const nodeId = this.u32();
        const low = this.u32();
        const high = this.u32();
        return { nodeId, baseRevision: BigInt(low) | (BigInt(high) << 32n) };
    }
    affinity() {
        const value = this.u8();
        assertAffinity(value);
        return value;
    }
    text() {
        const length = this.u32();
        if (length > MAX_RESOURCE_BYTES)
            fail("input text exceeds maximum size");
        const bytes = this.bytes(length);
        this.zeroes(padding(length));
        try {
            return utf8Decoder.decode(bytes);
        }
        catch (cause) {
            throw new InputStreamError("input text is not valid UTF-8", { cause });
        }
    }
    u8() {
        this.require(1);
        return this.#view.getUint8(this.#offset++);
    }
    u16() {
        this.require(2);
        const value = this.#view.getUint16(this.#offset, true);
        this.#offset += 2;
        return value;
    }
    u32() {
        this.require(4);
        const value = this.#view.getUint32(this.#offset, true);
        this.#offset += 4;
        return value;
    }
    f32() {
        this.require(4);
        const value = this.#view.getFloat32(this.#offset, true);
        this.#offset += 4;
        if (!Number.isFinite(value))
            fail("input contains a non-finite f32");
        return value;
    }
    bytes(length) {
        this.require(length);
        const result = this.#input.slice(this.#offset, this.#offset + length);
        this.#offset += length;
        return result;
    }
    zeroes(length) {
        if (this.bytes(length).some((byte) => byte !== 0))
            fail("reserved input bytes must be zero");
    }
    require(length) {
        if (!Number.isSafeInteger(length) || length < 0 || length > this.remaining) {
            fail("truncated input stream");
        }
    }
}
function validateInstructionSize(opcode, offset, end) {
    const layout = INPUT_LAYOUTS[opcode];
    const actual = end - offset;
    if (layout.fixedBytes !== null && actual !== layout.fixedBytes) {
        fail(`input opcode ${String(opcode)} consumed an invalid byte length`);
    }
    if (actual < layout.minimumBytes)
        fail(`input opcode ${String(opcode)} is too short`);
}
function assertAffinity(value) {
    if (!INPUT_AFFINITIES.has(value)) {
        fail(`unknown input affinity ${String(value)}`);
    }
}
function assertU32(value, label) {
    if (!Number.isInteger(value) || value < 0 || value > 0xffff_ffff) {
        fail(`${label} must be a u32`);
    }
}
function assertU64(value, label) {
    if (typeof value !== "bigint" || value < 0n || value > MAX_U64) {
        fail(`${label} must be a u64 bigint`);
    }
}
function assertScrollDelta(value, label) {
    if (!Number.isFinite(value) || Math.abs(value) > MAX_SCROLL_DELTA) {
        fail(`${label} exceeds the finite scroll delta bounds`);
    }
}
function padding(length) {
    return (PROTOCOL_ALIGNMENT - (length % PROTOCOL_ALIGNMENT)) % PROTOCOL_ALIGNMENT;
}
function fail(message) {
    throw new InputStreamError(message);
}
/** Whether an opcode byte names a member this build knows. */
function isKnownOpcode(values, value) {
    return typeof values[value] === "string";
}
//# sourceMappingURL=input-stream.js.map